import React from "react";
import { APICategory, LinkedIntegration } from "../../../models/Entities";
import { useHistory } from "react-router-dom";
import {
  navigateToProductionLinkedAccountsTablePage,
  PROD_LINKED_ACCOUNTS_PATH,
} from "../../../router/RouterUtils";
import { displayNameForAPICategoryIntegration } from "../../../models/Helpers";
import {
  Button,
  Card,
  Text,
  ButtonVariant,
  SkeletonLoader,
} from "@merge-api/merge-javascript-shared";
import { ChevronRight, CloudMoon } from "lucide-react";
import { conditionallyPluralize } from "../../../utils/general";

interface Props {
  integrations?: LinkedIntegration[];
}

const DashboardLinkedIntegrations = ({ integrations }: Props) => {
  const history = useHistory();

  return (
    <Card>
      <div className="p-4 border-b-solid border-b-gray-10 border-b-[1px] flex flex-row justify-between items-center">
        <Text variant="h5">Production integrations</Text>

        <Button
          variant={ButtonVariant.TextBlue}
          onClick={() => history.push(PROD_LINKED_ACCOUNTS_PATH)}
          rightIcon={<ChevronRight size={16} />}
        >
          View all integrations
        </Button>
      </div>
      <div className="p-4 flex flex-col gap-y-4">
        {integrations ? (
          integrations.length > 0 ? (
            integrations
              .sort((a, b) => (b.linked_accounts_count ?? 0) - (a.linked_accounts_count ?? 0))
              .map((integration: LinkedIntegration) => (
                <Card
                  type="button"
                  variant="outline"
                  className="px-5 py-4 flex flex-row justify-between hover:bg-gray-0"
                  onClick={() => {
                    navigateToProductionLinkedAccountsTablePage(history, {
                      integration__name: integration.name,
                    });
                  }}
                >
                  <div className="flex flex-row gap-x-2 items-center">
                    <img src={integration.square_image} className="w-10 h-10 rounded-full" />
                    <div className="flex flex-col gap-y-1 text-left">
                      <Text variant="h6">{integration.name}</Text>
                      <Text variant="sm" className="text-gray-40">
                        {conditionallyPluralize(
                          "Linked Account",
                          integration.linked_accounts_count ?? 0,
                        )}
                      </Text>
                    </div>
                  </div>
                  <div className="flex flex-col gap-y-1 h-full items-start">
                    {integration.categories.map((category) => (
                      <Text className="text-gray-40">
                        {displayNameForAPICategoryIntegration(
                          category as APICategory,
                          "pre-title",
                          12,
                        )}
                      </Text>
                    ))}
                  </div>
                </Card>
              ))
          ) : (
            <div className="flex flex-col items-center w-full py-12 text-gray-40 gap-y-4">
              <CloudMoon size={40} />
              <Text variant="h5">Hmm, no integrations yet</Text>
            </div>
          )
        ) : (
          <>
            {Array.from({ length: 3 }).map((_, i) => (
              <Card variant="outline" className="px-5 py-4 flex flex-row justify-between">
                <div className="flex flex-row gap-x-2 items-center">
                  <SkeletonLoader height={40} width={40} borderRadius={4} />
                  <div className="flex flex-col gap-y-1">
                    <SkeletonLoader height={14} width={230} borderRadius={4} />
                    <SkeletonLoader height={14} width={230} borderRadius={4} />
                  </div>
                </div>
                <SkeletonLoader height={14} width={100} borderRadius={4} />
              </Card>
            ))}
          </>
        )}
      </div>
    </Card>
  );
};

export default DashboardLinkedIntegrations;
